import React, { useState, useEffect } from "react";
import {
  adminProductService,
  CreateProductRequest,
} from "../../services/admin/productService";
import { Product } from "../../services/productService";
import { categoryService } from "../../services/categoryService";
import ProductForm from "../../components/admin/ProductForm";
import ConfirmModal from "../../components/admin/ConfirmModal";
import { TableSkeleton } from "../../components/common/LoadingSkeleton";
import Pagination from "../../components/common/Pagination";

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    loadProducts();
  }, [page]);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminProductService.getAll(page, 20);
      setProducts(response.products);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.message || "فشل تحميل المنتجات");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormError(null);
    setShowForm(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormError(null);
    setShowForm(true);
  };

  const handleDeleteClick = (product: Product) => {
    setProductToDelete({ id: product.id, name: product.name });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      await adminProductService.delete(productToDelete.id);
      setDeleteModalOpen(false);
      setProductToDelete(null);
      setError(null);
      await loadProducts();
    } catch (err: any) {
      setError(err.message || "فشل حذف المنتج");
      setDeleteModalOpen(false);
      setProductToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setProductToDelete(null);
  };


  const handleFormSubmit = async (data: CreateProductRequest) => {
    setFormError(null);
    setIsSubmitting(true);
    try {
      if (editingProduct) {
        // For updates, don't send images if array is empty (means "keep existing images")
        const updateData = { ...data };
        if (updateData.images && updateData.images.length === 0) {
          delete updateData.images;
        }
        await adminProductService.update(editingProduct.id, updateData);
      } else {
        await adminProductService.create(data);
      }
      setShowForm(false);
      setEditingProduct(null);
      setFormError(null);
      setError(null);
      await loadProducts();
    } catch (err: any) {
      const errorMessage = err.message || "فشل حفظ المنتج";
      setFormError(errorMessage);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    if (isSubmitting) return; // Prevent canceling while submitting
    setShowForm(false);
    setEditingProduct(null);
    setFormError(null);
    setIsSubmitting(false);
  };

  if (loading && products.length === 0) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
            إدارة المنتجات
          </h1>
          <div className="w-40 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        </div>
        <TableSkeleton rows={10} columns={6} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-[#0d171b] dark:text-white">
          إدارة المنتجات
        </h1>
        <button
          onClick={handleCreate}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
        >
          إضافة منتج جديد
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-red-600 dark:text-red-400 font-semibold mb-1">
                خطأ
              </p>
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {formError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-red-600 dark:text-red-400 font-semibold mb-1">
                خطأ في حفظ المنتج
              </p>
              <p className="text-red-600 dark:text-red-400 text-sm whitespace-pre-wrap">
                {formError}
              </p>
            </div>
            <button
              onClick={() => setFormError(null)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <ProductForm
          product={editingProduct}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          error={formError}
          isSubmitting={isSubmitting}
          onErrorClear={() => setFormError(null)}
        />
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  الصورة
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  الاسم
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  الفئة
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  السعر
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  المخزون
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {products.map((product) => (
                <tr
                  key={product.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <td className="px-6 py-4">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images[0].imageUrl}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded flex items-center justify-center">
                        <span className="text-xs text-slate-400">
                          لا توجد صورة
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#0d171b] dark:text-white">
                    {product.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    {product.category.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#0d171b] dark:text-white">
                    {Number(product.basePrice).toFixed(2)} د.ج
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded ${
                        product.inStock ?? true
                          ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                          : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                      }`}
                    >
                      {product.inStock ?? true ? "متوفر" : "نفذ من المخزون"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded ${
                        product.isActive
                          ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                          : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                      }`}
                    >
                      {product.isActive ? "نشط" : "غير نشط"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                      >
                        تعديل
                      </button>
                      <button
                        onClick={() => handleDeleteClick(product)}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {products.length === 0 && !loading && (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            لا توجد منتجات
          </div>
        )}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
            <Pagination
              currentPage={page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        title="تأكيد حذف المنتج"
        message={`هل أنت متأكد من حذف المنتج "${productToDelete?.name}"؟ لا يمكن التراجع عن هذا الإجراء.`}
        confirmText="نعم، حذف المنتج"
        cancelText="إلغاء"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
      />
    </div>
  );
};

export default Products;
